/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { FormattedDate } from 'react-intl'
import { capitalize } from 'lodash'

import type { EuiDescriptionListProps } from '@elastic/eui'
import { EuiDescriptionList, EuiFlexGrid, EuiFlexGroup, EuiFlexItem, EuiTitle } from '@elastic/eui'

import type { AnyCluster } from '@modules/ccm-lib/types'
import { FormattedMessage } from '@modules/i18n'

import CCMClusterNameDisplayAndEditor from './CCMClusterNameDisplayAndEditor'

import type { FC } from 'react'

type Props = {
  ccmCluster: AnyCluster
  canRenameCluster: boolean
  onClickRenameCluster: (clusterId: string, name: string) => void
  isSaving: boolean
}

const CCMClusterOverview: FC<Props> = ({
  ccmCluster,
  canRenameCluster,
  onClickRenameCluster,
  isSaving,
}) => (
  <EuiFlexGroup direction='column' gutterSize='m'>
    <EuiTitle size='s'>
      <h3>
        <FormattedMessage id='ccm.cluster-overview.title' defaultMessage='Overview' />
      </h3>
    </EuiTitle>

    <EuiFlexGrid columns={3} gutterSize='xl'>
      <EuiFlexItem>
        <OverviewDescriptionList
          listItems={[
            {
              title: (
                <FormattedMessage
                  id='ccm.cluster-overview.display-name'
                  defaultMessage='Cluster display name'
                />
              ),
              description: (
                <CCMClusterNameDisplayAndEditor
                  ccmCluster={ccmCluster}
                  canRenameCluster={canRenameCluster}
                  onSave={(name) => onClickRenameCluster(ccmCluster.id, name)}
                  isSaving={isSaving}
                />
              ),
            },
            {
              title: (
                <FormattedMessage
                  id='ccm.cluster-overview.cluster-id'
                  defaultMessage='Cluster ID'
                />
              ),
              description: ccmCluster.self_managed_cluster.id,
            },
          ]}
        />
      </EuiFlexItem>
      <EuiFlexItem>
        <OverviewDescriptionList
          listItems={[
            {
              title: (
                <FormattedMessage
                  id='ccm.cluster-overview.connected-at'
                  defaultMessage='Connected on'
                />
              ),
              description: (
                <FormattedDate
                  value={new Date(ccmCluster.metadata.created_at)}
                  year='numeric'
                  month='long'
                  day='numeric'
                />
              ),
            },
            {
              title: (
                <FormattedMessage
                  id='ccm.cluster-overview.cluster-version'
                  defaultMessage='Cluster version'
                />
              ),
              description: ccmCluster.self_managed_cluster.version,
            },
          ]}
        />
      </EuiFlexItem>
      <EuiFlexItem>
        <OverviewDescriptionList
          listItems={[
            {
              title: (
                <FormattedMessage id='ccm.cluster-overview.license-type' defaultMessage='License' />
              ),
              description: capitalize(ccmCluster.license.type),
            },
          ]}
        />
      </EuiFlexItem>
    </EuiFlexGrid>
  </EuiFlexGroup>
)

const OverviewDescriptionList: FC<EuiDescriptionListProps> = ({ children, ...props }) => (
  <EuiDescriptionList
    columnWidths={['0fr', '3fr']}
    type='column'
    columnGutterSize='m'
    titleProps={{ css: { fontSize: '14px', height: '40px', whiteSpace: 'nowrap' } }}
    {...props}
  >
    {children}
  </EuiDescriptionList>
)

export default CCMClusterOverview
